import React, { useState, useEffect } from 'react';
import { Form } from 'react-bootstrap';
import Select from 'react-select';
import { PARTICULAR_REQUEST_PARAMETER_LIST_GET } from '../../../../api/ApiConst';
import ApiCaller from '../../../../api/ApiCaller';
import HeadersForm from '../components/HeadersForm';
import RequestBodyForm from '../components/RequestBodyForm';
import ResponseParametersForm from '../components/ResponseParametersForm';

const CallApiAction = ({ 
    formData, 
    setFormData, 
    allTableList, 
    selectedRequest, 
    allPreviousSteps, 
    currentStepId 
}) => {
    const [headers, setHeaders] = useState(formData?.headers || []);
    const [requestBodyType, setRequestBodyType] = useState(formData?.requestBody?.type || formData?.requestBodyType || 'none');
    const [requestBodyAttributes, setRequestBodyAttributes] = useState(formData?.requestBody?.attributes || formData?.requestBodyAttributes || []);
    const [requestParameters, setRequestParameters] = useState([]);
    const [allRequestParameters, setAllRequestParameters] = useState([]);
    const [requestParamSource, setRequestParamSource] = useState(formData?.requestParamSource || ['custom_request']);
    const [responseParameters, setResponseParameters] = useState(formData?.responseParameters || []);

    // Effects
    useEffect(() => {
        // Clear existing parameters when sources change
        setRequestParameters([]);
        setAllRequestParameters([]);
        
        // Load parameters from all selected sources
        requestParamSource.forEach(source => {
            if (source === 'custom_request') {
                fetchRequestParameters(selectedRequest);
            } else {
                loadStepParameters(source);
            }
        });
    }, [requestParamSource, selectedRequest, allPreviousSteps]);

    // Ensure requestParameters are loaded when editing with existing data
    useEffect(() => {
        console.log(`Ensure requestParameters useEffect - formData.requestParamSource:`, formData?.requestParamSource);
        
        if (formData?.requestParamSource && formData.requestParamSource.length > 0) {
            console.log(`Ensure requestParameters useEffect - loading from requestParamSource:`, formData.requestParamSource);
            formData.requestParamSource.forEach(source => {
                if (source === 'custom_request') {
                    fetchRequestParameters(selectedRequest);
                } else {
                    loadStepParameters(source);
                }
            });
        }
    }, [formData?.requestParamSource, selectedRequest, allPreviousSteps]);

    // Initialize state from formData when editing
    useEffect(() => {
        if (formData?.headers) {
            setHeaders(formData.headers);
        }
    }, [formData?.headers]);

    useEffect(() => {
        if (formData?.requestBodyType) {
            setRequestBodyType(formData.requestBodyType);
        } else if (formData?.requestBody?.type) {
            setRequestBodyType(formData.requestBody.type);
        }
    }, [formData?.requestBodyType, formData?.requestBody?.type]);

    useEffect(() => {
        if (formData?.requestBodyAttributes) {
            setRequestBodyAttributes(formData.requestBodyAttributes);
        } else if (formData?.requestBody?.attributes) {
            setRequestBodyAttributes(formData.requestBody.attributes);
        }
    }, [formData?.requestBodyAttributes, formData?.requestBody?.attributes]);

    useEffect(() => {
        if (formData?.requestParamSource) {
            setRequestParamSource(formData.requestParamSource);
        }
    }, [formData?.requestParamSource]);

    useEffect(() => {
        if (formData?.responseParameters) {
            setResponseParameters(formData.responseParameters);
        }
    }, [formData?.responseParameters]);

    // Event Handlers
    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData((prev) => ({ ...prev, [name]: value }));
    };

    const handleRequestParamSourceChange = (selectedOptions) => {
        const values = selectedOptions ? selectedOptions?.map(option => option?.value) : ['custom_request'];
        setRequestParamSource(values);
        setFormData((prev) => ({
            ...prev,
            requestParamSource: values,
        }));
    };

    // API Functions
    const fetchRequestParameters = (requestId) => {
        if (!requestId) return;
        
        console.log(`fetchRequestParameters called with requestId: ${requestId}`);
        const apiUrl = PARTICULAR_REQUEST_PARAMETER_LIST_GET.replaceAll('{request-id}', requestId);
        console.log(`fetchRequestParameters - apiUrl: ${apiUrl}`);
        
        ApiCaller.callPostApi(
            apiUrl,
            null,
            {},
            (response) => {
                console.log(`fetchRequestParameters response:`, response);
                if (response?.data?.length > 0) {
                    const customRequestParams = response.data.map((item) => ({
                        value: `custom_request.${item.name}`,
                        label: `${item.name} (Custom Request)`,
                        type: item.type,
                        source: 'custom_request'
                    }));

                    // Merge with any existing step params already loaded in state
                    setRequestParameters(prev => {
                        const existingStepParams = (prev || []).filter(p => p?.source && String(p.source).startsWith('step_'));
                        const merged = [...customRequestParams, ...existingStepParams];
                        console.log(`fetchRequestParameters - customRequestParams:`, customRequestParams);
                        console.log(`fetchRequestParameters - existingStepParams:`, existingStepParams);
                        return merged;
                    });
                    setAllRequestParameters(response.data);
                } else {
                    // No custom params; keep whatever step params we have
                    setRequestParameters(prev => (prev || []).filter(p => p?.source && String(p.source).startsWith('step_')));
                }
            },
            (error) => {
                console.error('Error fetching request parameters:', error);
                // Keep existing step params on error
                setRequestParameters(prev => (prev || []).filter(p => p?.source && String(p.source).startsWith('step_')));
            }
        );
    };

    const loadStepParameters = (source) => {
        console.log(`loadStepParameters called with source: ${source}`);
        const stepMatch = source.match(/step_(\d+)/);
        if (!stepMatch) return;

        const stepId = parseInt(stepMatch[1]);
        const step = allPreviousSteps.find(s => s.id === stepId);
        if (!step || !step.actions || step.actions.length === 0) return;

        const action = step.actions[0];
        if (action.config?.responseParameters) {
            const stepParams = action.config.responseParameters.map(param => ({
                value: `step_${stepId}.${param.name}`,
                label: `Step ${stepId} - ${param.name}`,
                type: param.type,
                source: `step_${stepId}`
            }));
            
            // Merge with existing custom request params
            setRequestParameters(prev => {
                const existingCustomParams = (prev || []).filter(p => p?.source === 'custom_request');
                const merged = [...existingCustomParams, ...stepParams];
                console.log(`loadStepParameters - stepParams:`, stepParams);
                console.log(`loadStepParameters - existingCustomParams:`, existingCustomParams);
                return merged;
            });
        }
    };

    const getStepParameters = () => {
        let stepParams = [];
        requestParamSource.forEach(source => {
            if (source !== 'custom_request') {
                const stepAttributes = allPreviousSteps?.find(step => step.id === source)?.action?.config?.responseParameters;
                if (stepAttributes) {
                    stepParams = [...stepParams, ...stepAttributes.map(param => ({
                        value: param,
                        label: `${param?.name || param} (Step ${source})`
                    }))];
                }
            }
        });
        return stepParams;
    };

    // Render Components
    const renderRequestParameterSource = () => (
        <Form.Group className="mt-3">
            <Form.Label>Request Parameter Source</Form.Label>
            <Select
                isMulti
                options={[
                    { value: 'custom_request', label: 'Custom Request' },
                    ...(allPreviousSteps || [])?.map((step) => ({ 
                        value: step.id, 
                        label: "Step " + step.id 
                    })),
                ]}
                value={
                    requestParamSource
                        ? requestParamSource?.map(source =>
                            source === 'custom_request'
                                ? { value: 'custom_request', label: 'Custom Request' }
                                : allPreviousSteps
                                    ?.map((step) => ({ value: step.id, label: "Step " + step.id }))
                                    ?.find((opt) => opt.value === source)
                        ).filter(Boolean)
                        : []
                }
                onChange={handleRequestParamSourceChange}
                isClearable={false}
                className="react-select-container"
            />
        </Form.Group>
    );

    return (
        <>
            <Form.Group>
                <Form.Label>Method</Form.Label>
                <Form.Control 
                    as="select" 
                    name="method" 
                    value={formData?.method || ''} 
                    onChange={handleChange}
                >
                    <option value="">Select</option>
                    <option value="GET">GET</option>
                    <option value="POST">POST</option>
                    <option value="PUT">PUT</option>
                    <option value="DELETE">DELETE</option>
                </Form.Control>
            </Form.Group>
            <Form.Group className="mt-3">
                <Form.Label>Endpoint</Form.Label>
                <Form.Control 
                    type="text" 
                    name="endpoint" 
                    value={formData?.endpoint || ''} 
                    onChange={handleChange} 
                />
            </Form.Group>
            {renderRequestParameterSource()}

            <HeadersForm
                headers={headers}
                setHeaders={setHeaders}
                formData={formData}
                setFormData={setFormData}
            />

            <RequestBodyForm
                requestBodyType={requestBodyType}
                setRequestBodyType={setRequestBodyType}
                requestBodyAttributes={requestBodyAttributes}
                setRequestBodyAttributes={setRequestBodyAttributes}
                formData={formData}
                setFormData={setFormData}
                requestParameters={requestParameters}
            />

            <ResponseParametersForm
                responseParameters={responseParameters}
                setResponseParameters={setResponseParameters}
                formData={formData}
                setFormData={setFormData}
            />
        </>
    );
};

export default CallApiAction; 